import { c as _c } from "react/compiler-runtime";
import { THEMES, useThemeCntx } from "@/context/ThemeContext";
import { Color, Mesh, Program, Renderer, Triangle } from "ogl";
import { ReactNode, useEffect, useRef } from "react";
const vertexShader = `
attribute vec2 uv;
attribute vec2 position;

varying vec2 vUv;

void main() {
  vUv = uv;
  gl_Position = vec4(position, 0, 1);
}
`;
const fragmentShader = `
precision highp float;

uniform float uTime;
uniform vec3 uColor;
uniform vec3 uResolution;
uniform vec2 uMouse;
uniform float uAmplitude;
uniform float uSpeed;

varying vec2 vUv;

void main() {
  float mr = min(uResolution.x, uResolution.y);
  vec2 uv = (vUv.xy * 2.0 - 1.0) * uResolution.xy / mr;

  uv += (uMouse - vec2(0.5)) * uAmplitude;

  float d = -uTime * 0.5 * uSpeed;
  float a = 0.0;
  for (float i = 0.0; i < 8.0; ++i) {
    a += cos(i - d - a * uv.x);
    d += sin(uv.y * i + a);
  }
  d += uTime * 0.5 * uSpeed;
  vec3 col = vec3(cos(uv * vec2(d, a)) * 0.6 + 0.4, cos(a + d) * 0.5 + 0.5);
  col = cos(col * cos(vec3(d, a, 2.5)) * 0.5 + 0.5) * uColor;
  gl_FragColor = vec4(col, 1.0);
}
`;
interface IridescenceProps {
  colors?: [number, number, number];
  speed?: number;
  amplitude?: number;
  mouseReact?: boolean;
  className?: string;
  children?: ReactNode;
}

// Theme-based colors for Iridescence
const lightColors: [number, number, number] = [0.7, 0.7, 1.0];
const darkColors: [number, number, number] = [0.2, 0.2, 0.6];
export const Iridescence = t0 => {
  const $ = _c(27);
  let children;
  let colors;
  let rest;
  let t1;
  let t2;
  let t3;
  let t4;
  if ($[0] !== t0) {
    ({
      colors,
      speed: t1,
      amplitude: t2,
      mouseReact: t3,
      className: t4,
      children,
      ...rest
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = colors;
    $[3] = rest;
    $[4] = t1;
    $[5] = t2;
    $[6] = t3;
    $[7] = t4;
  } else {
    children = $[1];
    colors = $[2];
    rest = $[3];
    t1 = $[4];
    t2 = $[5];
    t3 = $[6];
    t4 = $[7];
  }
  const speed = t1 === undefined ? 1 : t1;
  const amplitude = t2 === undefined ? 0.1 : t2;
  const mouseReact = t3 === undefined ? true : t3;
  const className = t4 === undefined ? "" : t4;
  const {
    theme
  } = useThemeCntx();
  const ctnDom = useRef(null);
  let t5;
  if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = {
      x: 0.5,
      y: 0.5
    };
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  const mousePos = useRef(t5);
  const programRef = useRef(null);
  let t6;
  if ($[9] !== theme) {
    t6 = theme === THEMES.dark ? [...darkColors] : [...lightColors];
    $[9] = theme;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const themeColors = t6;
  const finalColors = colors || themeColors;
  const colorsRef = useRef(finalColors);
  let t7;
  let t8;
  if ($[11] !== finalColors) {
    t7 = () => {
      colorsRef.current = finalColors;
      if (programRef.current) {
        programRef.current.uniforms.uColor.value = new Color(...finalColors);
      }
    };
    t8 = [finalColors];
    $[11] = finalColors;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  useEffect(t7, t8);
  let t10;
  let t9;
  if ($[14] !== amplitude || $[15] !== mouseReact || $[16] !== speed) {
    t9 = () => {
      if (!ctnDom.current) {
        return;
      }
      const ctn = ctnDom.current;
      const renderer = new Renderer();
      const gl = renderer.gl;
      gl.clearColor(0, 0, 0, 0);
      const resize = function resize() {
        renderer.setSize(ctn.offsetWidth * 1, ctn.offsetHeight * 1);
        if (programRef.current) {
          programRef.current.uniforms.uResolution.value = new Color(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height);
        }
      };
      window.addEventListener("resize", resize, false);
      resize();
      const geometry = new Triangle(gl);
      const program = new Program(gl, {
        vertex: vertexShader,
        fragment: fragmentShader,
        uniforms: {
          uTime: {
            value: 0
          },
          uColor: {
            value: new Color(...colorsRef.current)
          },
          uResolution: {
            value: new Color(gl.canvas.width, gl.canvas.height, gl.canvas.width / gl.canvas.height)
          },
          uMouse: {
            value: new Float32Array([mousePos.current.x, mousePos.current.y])
          },
          uAmplitude: {
            value: amplitude
          },
          uSpeed: {
            value: speed
          }
        }
      });
      programRef.current = program;
      const mesh = new Mesh(gl, {
        geometry,
        program
      });
      let animateId;
      function update(t) {
        animateId = requestAnimationFrame(update);
        if (programRef.current) {
          programRef.current.uniforms.uTime.value = t * 0.001;
          programRef.current.uniforms.uColor.value = new Color(...colorsRef.current);
        }
        renderer.render({
          scene: mesh
        });
      }
      animateId = requestAnimationFrame(update);
      gl.canvas.style.position = "absolute";
      gl.canvas.style.top = "0";
      gl.canvas.style.left = "0";
      gl.canvas.style.width = "100%";
      gl.canvas.style.height = "100%";
      gl.canvas.style.pointerEvents = "none";
      ctn.appendChild(gl.canvas);
      const handleMouseMove = function handleMouseMove(e) {
        const rect = ctn.getBoundingClientRect();
        const x = (e.clientX - rect.left) / rect.width;
        const y = 1 - (e.clientY - rect.top) / rect.height;
        mousePos.current = {
          x,
          y
        };
        if (programRef.current) {
          programRef.current.uniforms.uMouse.value[0] = x;
          programRef.current.uniforms.uMouse.value[1] = y;
        }
      };
      if (mouseReact) {
        ctn.addEventListener("mousemove", handleMouseMove);
      }
      return () => {
        cancelAnimationFrame(animateId);
        window.removeEventListener("resize", resize);
        if (mouseReact) {
          ctn.removeEventListener("mousemove", handleMouseMove);
        }
        if (ctn.contains(gl.canvas)) {
          ctn.removeChild(gl.canvas);
        }
        gl.getExtension("WEBGL_lose_context")?.loseContext();
      };
    };
    t10 = [speed, amplitude, mouseReact];
    $[14] = amplitude;
    $[15] = mouseReact;
    $[16] = speed;
    $[17] = t10;
    $[18] = t9;
  } else {
    t10 = $[17];
    t9 = $[18];
  }
  useEffect(t9, t10);
  const t11 = `fixed inset-0 w-screen h-screen overflow-hidden transform-gpu ${className}`;
  let t12;
  if ($[19] !== rest || $[20] !== t11) {
    t12 = <div ref={ctnDom} className={t11} {...rest} />;
    $[19] = rest;
    $[20] = t11;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== children) {
    t13 = children && <div className="relative z-10 w-full">{children}</div>;
    $[22] = children;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = <>{t12}{t13}</>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  return t14;
};